import { useCallback, useEffect, useState } from 'react';
import * as Icons from '../icons';
import { titleCase, highestEducation, compactNumber } from '../util';
import { ProfileDrawer } from '../components/ProfileDrawer';
import { FiltersModal } from '../components/FiltersModal';
import { CriteriaModal } from '../components/CriteriaModal';
import type { Contact, SearchResult, Criterion } from '../types';
import {
  runAgent,
  runResults,
  updateSearch,
  setProspectState,
  unsetProspectState,
  listProspectsByState,
} from '../api';
import type { SearchDoc, ResultsWarning } from '../api';

interface Props {
  search: SearchDoc;
  filters: Record<string, unknown>;
  criteria: Criterion[];
  matchCount: number;
  onChange: (next: { filters: Record<string, unknown>; criteria: Criterion[]; matchCount: number }) => void;
  onNewSearch: () => void;
  onReview: (results: SearchResult[]) => void;
}

const PAGE_SIZE = 3;

const pillBtnCls =
  'inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] bg-[var(--surface)] px-3.5 py-1.5 text-sm text-[var(--text-primary)] hover:border-[var(--accent)]';

const iconBtnCls =
  'inline-flex h-8 w-8 items-center justify-center rounded-md border border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)] disabled:cursor-not-allowed disabled:opacity-50';

const tabCls = (active: boolean) =>
  'border-0 bg-transparent py-3 text-sm transition-colors flex items-center gap-1.5 ' +
  (active
    ? 'border-b-2 border-[var(--accent)] font-medium text-[var(--text-primary)]'
    : 'border-b-2 border-transparent text-[var(--text-muted)]');

const toolbarIconBtnCls = (active: boolean) =>
  'inline-flex h-7 w-7 items-center justify-center rounded-sm border bg-[var(--surface)] ' +
  (active
    ? 'border-[var(--accent)] bg-[var(--accent-muted)] text-[var(--accent-strong)]'
    : 'border-[var(--border-default)] text-[var(--text-muted)]');

type Tab = 'results' | 'shortlist' | 'insights';

export function ResultsView({ search, filters, criteria, matchCount, onChange, onNewSearch, onReview }: Props) {
  const [openContact, setOpenContact] = useState<Contact | null>(null);
  const [openIdx, setOpenIdx] = useState<number>(-1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [criteriaOpen, setCriteriaOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('results');
  const [view, setView] = useState<'list' | 'card'>('list');
  const [page, setPage] = useState(1);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [total, setTotal] = useState(matchCount);
  const [analyzing, setAnalyzing] = useState(false);
  const [mode, setMode] = useState<'live' | 'demo'>('demo');
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<ResultsWarning | null>(null);
  const [cached, setCached] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0);
  const [shortlist, setShortlist] = useState<SearchResult[]>([]);
  const [shortlistLoading, setShortlistLoading] = useState(false);
  const [shortlistCount, setShortlistCount] = useState(0);

  const filterCount = countActiveFilters(filters);
  const [promptDraft, setPromptDraft] = useState(search.prompt);
  const [reparseLoading, setReparseLoading] = useState(false);

  // Keep input in sync if the active search changes (e.g. user picked a different one).
  useEffect(() => {
    setPromptDraft(search.prompt);
  }, [search._id, search.prompt]);

  const reparsePrompt = async () => {
    const next = promptDraft.trim();
    if (!next || next === search.prompt) return;
    setReparseLoading(true);
    try {
      const r = await runAgent(next);
      const nextFilters = r.interpretation?.filters || {};
      const nextCriteria = r.interpretation?.criteria || [];
      onChange({ filters: nextFilters, criteria: nextCriteria, matchCount });
      await updateSearch(search._id, { prompt: next, filters: nextFilters, criteria: nextCriteria });
      setPage(1);
    } catch (e) {
      console.warn('[reparse]', e);
    } finally {
      setReparseLoading(false);
    }
  };

  const loadShortlist = useCallback(async () => {
    setShortlistLoading(true);
    try {
      const records = await listProspectsByState(search._id, 'shortlisted', 200);
      const items: SearchResult[] = records
        .filter(r => r.snapshot)
        .map(r => ({
          contact: { ...(r.snapshot as Contact), recruiterStates: ['shortlisted'] },
          reasons: [],
        }));
      setShortlist(items);
      setShortlistCount(items.length);
    } catch (e) {
      console.warn('[shortlist] load error:', e);
    } finally {
      setShortlistLoading(false);
    }
  }, [search._id]);

  useEffect(() => {
    let cancelled = false;
    setAnalyzing(true);
    setError(null);
    const wantRefresh = forceRefresh > 0;
    runResults(search._id, { page, pageSize: PAGE_SIZE, evaluate: true, refresh: wantRefresh })
      .then(res => {
        if (cancelled) return;
        setResults(res.results);
        setTotal(res.total);
        setMode(res.mode);
        setWarning(res.warning || null);
        setCached(!!res.cached);
      })
      .catch(e => {
        if (cancelled) return;
        console.warn('[results] server error:', e?.message || e);
        setError('Server error — try again');
        setResults([]);
        setTotal(0);
        setWarning(null);
        setCached(false);
      })
      .finally(() => {
        if (!cancelled) setAnalyzing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, search._id, filters, criteria, forceRefresh]);

  // Load shortlist count on mount + whenever the search changes.
  useEffect(() => {
    loadShortlist();
  }, [loadShortlist]);

  const start = (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(start + PAGE_SIZE - 1, total);

  const updateContactStateLocally = useCallback(
    (id: string, mutate: (states: string[]) => string[]) => {
      setResults(rs =>
        rs.map(r =>
          r.contact.id === id
            ? { ...r, contact: { ...r.contact, recruiterStates: mutate(r.contact.recruiterStates || []) } }
            : r,
        ),
      );
      setShortlist(rs =>
        rs.map(r =>
          r.contact.id === id
            ? { ...r, contact: { ...r.contact, recruiterStates: mutate(r.contact.recruiterStates || []) } }
            : r,
        ),
      );
      // Mirror to drawer if it's showing this contact.
      setOpenContact(c => (c && c.id === id ? { ...c, recruiterStates: mutate(c.recruiterStates || []) } : c));
    },
    [],
  );

  const open = (i: number, source: SearchResult[]) => {
    const r = source[i];
    setOpenContact(r.contact);
    setOpenIdx(i);
    // Auto-mark viewed (idempotent upsert; ignore failures silently).
    if (!(r.contact.recruiterStates || []).includes('viewed')) {
      setProspectState(search._id, r.contact.id, 'viewed', { snapshot: r.contact }).catch(() => {});
      updateContactStateLocally(r.contact.id, states => Array.from(new Set([...states, 'viewed'])));
    }
  };

  const handleSaveFilters = async (next: Record<string, unknown>) => {
    onChange({ filters: next, criteria, matchCount });
    try {
      await updateSearch(search._id, { filters: next });
      setPage(1);
    } catch (e) {
      console.warn('[updateSearch]', e);
    }
  };
  const handleSaveCriteria = async (next: Criterion[]) => {
    onChange({ filters, criteria: next, matchCount });
    try {
      await updateSearch(search._id, { criteria: next });
      setPage(1);
    } catch (e) {
      console.warn('[updateSearch]', e);
    }
  };

  const toggleShortlist = async (contact: Contact) => {
    const isShortlisted = (contact.recruiterStates || []).includes('shortlisted');
    if (isShortlisted) {
      updateContactStateLocally(contact.id, s => s.filter(x => x !== 'shortlisted'));
      try {
        await unsetProspectState(search._id, contact.id, 'shortlisted');
        await loadShortlist();
      } catch (e) {
        console.warn('[unshortlist]', e);
        updateContactStateLocally(contact.id, s => Array.from(new Set([...s, 'shortlisted'])));
      }
    } else {
      updateContactStateLocally(contact.id, s => Array.from(new Set([...s, 'shortlisted'])));
      try {
        await setProspectState(search._id, contact.id, 'shortlisted', { snapshot: contact });
        await loadShortlist();
      } catch (e) {
        console.warn('[shortlist]', e);
        updateContactStateLocally(contact.id, s => s.filter(x => x !== 'shortlisted'));
      }
    }
  };
  const toggleHide = async (contact: Contact) => {
    const isHidden = (contact.recruiterStates || []).includes('hidden');
    if (isHidden) {
      updateContactStateLocally(contact.id, s => s.filter(x => x !== 'hidden'));
      try {
        await unsetProspectState(search._id, contact.id, 'hidden');
      } catch (e) {
        console.warn('[unhide]', e);
        updateContactStateLocally(contact.id, s => Array.from(new Set([...s, 'hidden'])));
      }
    } else {
      // Reversible: row dims in-place rather than disappearing. Click the eye again to restore.
      updateContactStateLocally(contact.id, s => Array.from(new Set([...s, 'hidden'])));
      try {
        await setProspectState(search._id, contact.id, 'hidden', { snapshot: contact });
      } catch (e) {
        console.warn('[hide]', e);
        updateContactStateLocally(contact.id, s => s.filter(x => x !== 'hidden'));
      }
    }
  };

  const visible = tab === 'shortlist' ? shortlist : results;

  return (
    <div className="flex flex-1 flex-col">
      <header className="sticky top-0 z-10 flex h-[52px] items-center justify-between border-b border-[var(--border-default)] bg-[var(--surface)] px-6">
        <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
          <span>First Project</span>
          <span className="text-[var(--text-tertiary)]">›</span>
          <span>Searches</span>
          <span className="text-[var(--text-tertiary)]">›</span>
          <b className="font-medium text-[var(--text-primary)]">{search.title}</b>
          <span className="ml-3 text-[12px] text-[var(--text-muted)]">
            {mode === 'live' ? '● live' : '○ demo'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border-default)] bg-[var(--surface)] px-3 py-1.5 text-sm hover:bg-[var(--surface-muted)]">
            <Icons.Share /> Share
          </button>
          <button
            className="inline-flex items-center gap-1.5 rounded-md border border-[var(--accent)] bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--accent-strong)]"
            onClick={onNewSearch}
          >
            <Icons.Plus /> New Search
          </button>
        </div>
      </header>

      <div className="flex items-center gap-3 border-b border-[var(--border-default)] bg-[var(--surface)] px-6 py-3">
        <div className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-[var(--surface-tertiary)] text-[var(--text-muted)]">
          <Icons.People />
        </div>
        <input
          className="flex-1 rounded-md border border-[var(--border-default)] bg-[var(--surface)] px-3 py-1.5 text-sm outline-none focus:border-[var(--accent)] disabled:opacity-50"
          value={promptDraft}
          onChange={e => setPromptDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !reparseLoading) {
              e.preventDefault();
              reparsePrompt();
            }
          }}
          onBlur={() => {
            if (promptDraft.trim() && promptDraft.trim() !== search.prompt) reparsePrompt();
          }}
          disabled={reparseLoading}
          title="Edit prompt and press Enter to re-parse with Grok"
          placeholder="Edit your search prompt…"
        />
        {reparseLoading ? (
          <span className="text-[12px] text-[var(--text-muted)]">Re-parsing…</span>
        ) : null}
        <button className={pillBtnCls} onClick={() => setFiltersOpen(true)}>
          <Icons.Funnel /> Filters{' '}
          <span className="rounded-full bg-[var(--accent-muted)] px-2 py-0.5 text-[12px] font-medium text-[var(--accent-strong)]">
            {filterCount}
          </span>
        </button>
        <button className={pillBtnCls} onClick={() => setCriteriaOpen(true)}>
          <Icons.Diamond /> Criteria{' '}
          <span className="rounded-full bg-[var(--accent-muted)] px-2 py-0.5 text-[12px] font-medium text-[var(--accent-strong)]">
            {criteria.length}
          </span>
        </button>
      </div>

      <nav className="flex gap-6 border-b border-[var(--border-default)] bg-[var(--surface)] px-6">
        <button className={tabCls(tab === 'results')} onClick={() => setTab('results')}>
          Results
        </button>
        <button className={tabCls(tab === 'shortlist')} onClick={() => setTab('shortlist')}>
          <Icons.Bookmark style={{ width: 14, height: 14 }} />
          Shortlist
          {shortlistCount > 0 ? (
            <span className="ml-0.5 rounded-full bg-[var(--accent-muted)] px-1.5 py-0 text-[11px] font-medium text-[var(--accent-strong)]">
              {shortlistCount}
            </span>
          ) : null}
        </button>
        <button className={tabCls(tab === 'insights')} onClick={() => setTab('insights')}>
          Insights
        </button>
      </nav>

      {tab !== 'shortlist' ? (
        <div className="flex items-center justify-between border-b border-[var(--border-muted)] bg-[var(--surface)] px-6 py-3">
          <div className="flex items-center gap-3 text-sm">
            <input type="checkbox" style={{ accentColor: 'var(--accent)' }} />
            <Icons.ChevronDown style={{ width: 14, height: 14, color: 'var(--text-muted)' }} />
            <span className="ml-1">Matches ({compactNumber(total)})</span>
            <button className={toolbarIconBtnCls(view === 'list')} onClick={() => setView('list')} title="List view">
              <Icons.ListView />
            </button>
            <button className={toolbarIconBtnCls(view === 'card')} onClick={() => setView('card')} title="Card view">
              <Icons.GridView />
            </button>
            <button
              className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border-default)] bg-[var(--surface)] px-2.5 py-1 text-[13px] hover:bg-[var(--surface-muted)] disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!results.length}
              onClick={() => onReview(results)}
            >
              <Icons.Eye style={{ width: 14, height: 14 }} /> Review
            </button>
            {analyzing ? (
              <span className="ml-2 text-[12px] text-[var(--text-muted)]">
                Analyzing {PAGE_SIZE} prospects with Grok…
              </span>
            ) : cached ? (
              <span
                className="ml-2 inline-flex items-center gap-1 rounded-sm bg-[var(--surface-tertiary)] px-1.5 py-0.5 text-[11px] text-[var(--text-muted)]"
                title="Showing cached results — no Coresignal credits used"
              >
                <Icons.Bolt style={{ width: 10, height: 10 }} /> Cached
              </span>
            ) : null}
            <button
              className="ml-1 inline-flex items-center gap-1 rounded-md border border-[var(--border-default)] bg-[var(--surface)] px-2 py-1 text-[12px] text-[var(--text-muted)] hover:bg-[var(--surface-muted)] hover:text-[var(--text-primary)] disabled:opacity-50"
              onClick={() => setForceRefresh(n => n + 1)}
              disabled={analyzing}
              title="Re-fetch live from Coresignal (uses credits)"
            >
              Refresh
            </button>
            {error ? (
              <span className="ml-2 text-[12px] text-rose-700">{error}</span>
            ) : null}
          </div>
          <div className="flex items-center gap-3 text-sm">
            <button
              className={iconBtnCls}
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              title="Prev page"
            >
              <Icons.ChevronLeft />
            </button>
            <span className="mx-2">
              {start} – {end} of {compactNumber(total)}
            </span>
            <button
              className={iconBtnCls}
              disabled={end >= total}
              onClick={() => setPage(p => p + 1)}
              title="Next page"
            >
              <Icons.ChevronRight />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between border-b border-[var(--border-muted)] bg-[var(--surface)] px-6 py-3">
          <div className="flex items-center gap-3 text-sm">
            <Icons.Bookmark style={{ width: 14, height: 14 }} />
            <span>Shortlisted ({shortlistCount})</span>
            {shortlistLoading ? (
              <span className="text-[12px] text-[var(--text-muted)]">Loading…</span>
            ) : null}
          </div>
          <button
            className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border-default)] bg-[var(--surface)] px-2.5 py-1 text-[13px] hover:bg-[var(--surface-muted)]"
            onClick={loadShortlist}
          >
            Refresh
          </button>
        </div>
      )}

      {tab === 'insights' ? (
        <div className="flex flex-1 items-center justify-center bg-[var(--surface)] p-12 text-[var(--text-muted)]">
          Insights coming soon — distribution of seniority, industry, location across this result set.
        </div>
      ) : tab === 'shortlist' && !shortlist.length ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-[var(--surface)] p-12 text-[var(--text-muted)]">
          <Icons.Bookmark style={{ width: 32, height: 32 }} />
          <div className="text-base">No shortlisted prospects yet.</div>
          <div className="text-[13px]">Click the bookmark on any result row to add to your shortlist.</div>
        </div>
      ) : tab === 'results' && !analyzing && !visible.length && total === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-[var(--surface)] p-12 text-[var(--text-muted)]">
          <Icons.Search style={{ width: 32, height: 32 }} />
          <div className="text-base">No prospects matched.</div>
          <div className="text-[13px]">
            Try widening filters{filterCount ? ` (${filterCount} active)` : ''} or rephrasing the prompt.
          </div>
          <button
            className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-[var(--border-default)] bg-[var(--surface)] px-3 py-1.5 text-sm hover:bg-[var(--surface-muted)]"
            onClick={() => setFiltersOpen(true)}
          >
            <Icons.Funnel style={{ width: 14, height: 14 }} /> Edit filters
          </button>
        </div>
      ) : tab === 'results' && !analyzing && !visible.length && total > 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-[var(--surface)] p-12 text-[var(--text-muted)]">
          <Icons.Info style={{ width: 32, height: 32 }} />
          {warning?.code === 'billing_credits' ? (
            <>
              <div className="text-base text-[var(--fg-warning)]">Coresignal account is out of credits.</div>
              <div className="max-w-md text-center text-[13px]">
                Search returned {compactNumber(total)} matches, but profile data can't be fetched until credits are refreshed at{' '}
                <a className="text-[var(--text-link)] hover:underline" href="https://dashboard.coresignal.com/" target="_blank" rel="noreferrer">
                  dashboard.coresignal.com
                </a>
                .
              </div>
            </>
          ) : warning?.code === 'rate_limit' ? (
            <>
              <div className="text-base">Coresignal rate limit hit.</div>
              <div className="text-[13px]">{warning.message} Try again in a moment.</div>
            </>
          ) : warning ? (
            <>
              <div className="text-base">Couldn't load profile data.</div>
              <div className="max-w-md text-center text-[13px]">{warning.message}</div>
            </>
          ) : (
            <>
              <div className="text-base">Couldn't load profile data on this page.</div>
              <div className="text-[13px]">
                Coresignal returned {compactNumber(total)} matching IDs but didn't return profiles. Try the next page or refresh.
              </div>
            </>
          )}
          <div className="mt-2 flex gap-2">
            <button
              className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border-default)] bg-[var(--surface)] px-3 py-1.5 text-sm hover:bg-[var(--surface-muted)]"
              onClick={() => setPage(p => p + 1)}
              disabled={end >= total}
            >
              Next page
            </button>
            <button
              className="inline-flex items-center gap-1.5 rounded-md border border-[var(--accent)] bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--accent-strong)]"
              onClick={() => setPage(1)}
            >
              Retry
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto bg-[var(--surface)]">
          {visible.map((r, i) => (
            <ResultRow
              key={r.contact.id + ':' + i}
              r={r}
              onClick={() => open(i, visible)}
              analyzing={tab === 'results' && analyzing}
              onShortlist={() => toggleShortlist(r.contact)}
              onHide={() => toggleHide(r.contact)}
            />
          ))}
        </div>
      )}

      <ProfileDrawer
        contact={openContact}
        onClose={() => {
          setOpenContact(null);
          setOpenIdx(-1);
        }}
        onPrev={openIdx > 0 ? () => open(openIdx - 1, visible) : undefined}
        onNext={openIdx >= 0 && openIdx < visible.length - 1 ? () => open(openIdx + 1, visible) : undefined}
        onToggleShortlist={openContact ? () => toggleShortlist(openContact) : undefined}
      />
      <FiltersModal
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        matchCount={total}
        initialValues={filters}
        onSave={handleSaveFilters}
      />
      <CriteriaModal
        open={criteriaOpen}
        onClose={() => setCriteriaOpen(false)}
        initial={criteria}
        onSave={handleSaveCriteria}
      />
    </div>
  );
}

function ResultRow({
  r,
  onClick,
  analyzing,
  onShortlist,
  onHide,
}: {
  r: SearchResult;
  onClick: () => void;
  analyzing: boolean;
  onShortlist: () => void;
  onHide: () => void;
}) {
  const p = r.contact.profileData;
  const name = titleCase(p.full_name);
  const edu = highestEducation(r.contact);
  const inATS = r.contact.statuses?.some(s => s.statusId);
  const states = r.contact.recruiterStates || [];
  const isShortlisted = states.includes('shortlisted');
  const isViewed = states.includes('viewed');
  const isContacted = states.includes('contacted');
  const isHidden = states.includes('hidden');

  return (
    <div
      className={
        'flex cursor-pointer gap-3.5 border-b border-[var(--border-muted)] px-6 py-4 transition-colors hover:bg-[var(--surface-muted)] ' +
        (isHidden ? 'opacity-50 hover:opacity-75' : '')
      }
      onClick={onClick}
    >
      <div className="flex items-start pt-0.5" onClick={e => e.stopPropagation()}>
        <input type="checkbox" style={{ accentColor: 'var(--accent)', width: 16, height: 16 }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-1.5 flex items-center gap-2">
          <span className="text-[15px] font-medium text-[var(--accent-strong)]">{name}</span>
          {inATS ? (
            <span
              className="inline-flex h-4 w-4 items-center justify-center rounded-sm bg-[var(--surface-tertiary)] text-[var(--text-muted)]"
              title="In ATS"
            >
              <Icons.Assignment style={{ width: 10, height: 10 }} />
            </span>
          ) : null}
          {p.linkedin_url ? (
            <span
              className="inline-flex h-4 w-4 items-center justify-center rounded-sm text-white"
              style={{ background: '#0a66c2' }}
            >
              <Icons.LinkedIn style={{ width: 10, height: 10 }} />
            </span>
          ) : null}
          {/* State pips */}
          {isShortlisted ? <StatePip kind="shortlisted" /> : null}
          {isContacted ? <StatePip kind="contacted" /> : null}
          {isHidden ? <StatePip kind="hidden" /> : null}
          {isViewed && !isShortlisted && !isContacted ? <StatePip kind="viewed" /> : null}
        </div>
        <div className="mb-1 flex flex-wrap items-center gap-2 text-[13px] text-[var(--text-muted)] leading-5">
          <Icons.Work style={{ width: 13, height: 13, flexShrink: 0, position: 'relative', top: 1 }} />
          <span>
            <span className="text-[var(--text-primary)]">{titleCase(p.job_title)}</span>
            {p.job_company_name ? <> at {titleCase(p.job_company_name)}</> : null}
          </span>
          {p.location_name ? (
            <>
              <span className="text-[var(--text-tertiary)] leading-none">•</span>
              <span>{titleCase(p.location_name)}</span>
            </>
          ) : null}
        </div>
        {edu.school ? (
          <div className="mb-1 flex flex-wrap items-center gap-2 text-[13px] text-[var(--text-muted)] leading-5">
            <Icons.School style={{ width: 13, height: 13, flexShrink: 0, position: 'relative', top: 1 }} />
            <span>
              {edu.degree}
              {edu.major ? ', ' + edu.major : ''} at {edu.school}
            </span>
          </div>
        ) : null}
        {r.reasons.length ? (
          r.reasons.map((reason, ri) => (
            <div key={ri} className="mt-2 flex items-start gap-2 text-[13px] text-[var(--text-muted)] leading-5">
              <span className={reasonChipCls(reason.verdict)}>
                {reason.verdict === 'no' ? (
                  <Icons.Ban style={{ width: 11, height: 11 }} />
                ) : (
                  <Icons.ThumbUp style={{ width: 11, height: 11 }} />
                )}
                <span>{reason.label}</span>
              </span>
              <span className="flex-1">{reason.rationale}</span>
            </div>
          ))
        ) : analyzing ? (
          <div className="mt-2 text-[12px] text-[var(--text-muted)]">Evaluating…</div>
        ) : null}
      </div>
      <div className="flex flex-none items-start gap-2 pt-0.5" onClick={e => e.stopPropagation()}>
        <button
          className={hideButtonCls(isHidden)}
          onClick={onHide}
          title={isHidden ? 'Show' : 'Hide'}
        >
          <Icons.Eye style={{ width: 12, height: 12 }} />
          {isHidden ? 'Show' : 'Hide'}
        </button>
        <button
          className={shortlistButtonCls(isShortlisted)}
          onClick={onShortlist}
          title={isShortlisted ? 'Remove from shortlist' : 'Add to shortlist'}
        >
          <Icons.Bookmark style={{ width: 12, height: 12 }} />
          {isShortlisted ? 'Shortlisted' : 'Shortlist'}
        </button>
      </div>
    </div>
  );
}

function hideButtonCls(active: boolean): string {
  const base = 'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[13px] transition-colors';
  if (active) {
    return base + ' border-[var(--border-emphasis)] bg-[var(--surface-tertiary)] text-[var(--text-primary)] hover:bg-[var(--surface-muted)]';
  }
  return base + ' border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--surface-muted)] hover:text-[var(--text-primary)]';
}

function shortlistButtonCls(active: boolean): string {
  const base = 'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[13px] transition-colors';
  if (active) {
    return base + ' border-[var(--accent)] bg-[var(--accent-muted)] text-[var(--accent-strong)] font-medium hover:bg-[var(--accent-subtle)]';
  }
  return base + ' border-[var(--border-default)] bg-[var(--surface)] hover:bg-[var(--surface-muted)]';
}

function StatePip({ kind }: { kind: 'shortlisted' | 'viewed' | 'contacted' | 'hidden' }) {
  if (kind === 'shortlisted') {
    return (
      <span className="inline-flex items-center gap-1 rounded-sm bg-[var(--accent-muted)] px-1.5 py-0.5 text-[11px] font-medium text-[var(--accent-strong)]">
        <Icons.Bookmark style={{ width: 10, height: 10 }} /> Shortlisted
      </span>
    );
  }
  if (kind === 'contacted') {
    return (
      <span className="inline-flex items-center gap-1 rounded-sm bg-[var(--bg-info)] px-1.5 py-0.5 text-[11px] font-medium text-[var(--fg-info)]">
        <Icons.Send style={{ width: 10, height: 10 }} /> Contacted
      </span>
    );
  }
  if (kind === 'hidden') {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-sm bg-[var(--surface-tertiary)] px-1.5 py-0.5 text-[11px] font-medium text-[var(--text-muted)]"
        title="Hidden from your default view"
      >
        <Icons.Eye style={{ width: 10, height: 10 }} /> Hidden
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[11px] text-[var(--text-tertiary)]"
      title="You've viewed this prospect"
    >
      <Icons.Eye style={{ width: 10, height: 10 }} /> Viewed
    </span>
  );
}

function reasonChipCls(verdict: 'good' | 'partial' | 'no'): string {
  const base = 'inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[12px] font-medium [&_svg]:h-3 [&_svg]:w-3';
  if (verdict === 'good') return base + ' bg-[var(--bg-success)] text-[var(--fg-success)]';
  if (verdict === 'partial') return base + ' bg-[var(--bg-warning)] text-[var(--fg-warning)]';
  return base + ' bg-[var(--bg-danger)] text-[var(--fg-danger)]';
}

function countActiveFilters(values: Record<string, unknown>): number {
  let n = 0;
  for (const [k, v] of Object.entries(values)) {
    if (k.endsWith('MatchType')) continue;
    if (v == null || v === '' || v === 'none') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    if (typeof v === 'object' && v !== null && !Array.isArray(v) && Object.keys(v).length === 0) continue;
    n++;
  }
  return n;
}
